// Utility functions for M500 zone handling

export const validateM500Zone = (obj: any) => {
  try {
    if (!obj) {
      throw new Error("Invalid input");
    }

    // Already a zone object from the API
    if (typeof obj === "object" && !Array.isArray(obj)) {
      return {
        PipeNum: obj?.PipeNum ?? obj?.pipeNum ?? "-",
        PassNum: obj?.PassNum ?? obj?.passNum ?? "-",
        ZoneNum: obj?.ZoneNum ?? obj?.zoneNum ?? "-",
      };
    }

    if (typeof obj === "string") {
      const trimmed = obj.trim();
      // Plain label (e.g. "Zone 1") — not JSON
      if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
        return { PipeNum: "-", PassNum: "-", ZoneNum: trimmed || "-" };
      }
      const parsedObj = JSON.parse(trimmed);
      return {
        PipeNum: parsedObj?.PipeNum ?? "-",
        PassNum: parsedObj?.PassNum ?? "-",
        ZoneNum: parsedObj?.ZoneNum ?? "-",
      };
    }

    throw new Error("Unsupported zone shape");
  } catch (error) {
    return { PipeNum: "-", PassNum: "-", ZoneNum: "-" };
  }
};

// Helper function to check if a value is valid (not "!" and not null/undefined/empty)
const isValidValue = (val: any): boolean => {
  return val !== null && val !== undefined && val !== "" && val !== "!";
};

/** Format zone_name / zone for table display (string, JSON string, or object). */
export const formatZoneDisplayValue = (rawZone: unknown): string => {
  if (rawZone == null || rawZone === "") return "";
  if (typeof rawZone === "object" && !Array.isArray(rawZone)) {
    const zoneNum = (rawZone as any).ZoneNum ?? (rawZone as any).zoneNum;
    if (zoneNum != null && zoneNum !== "") return `Zone ${zoneNum}`;
    return "";
  }
  if (typeof rawZone !== "string") return String(rawZone);
  const trimmed = rawZone.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      const zoneNum = parsed?.ZoneNum ?? parsed?.zoneNum;
      if (zoneNum != null && zoneNum !== "") return `Zone ${zoneNum}`;
    } catch {
      return trimmed;
    }
  }
  return trimmed;
};

export const extractZoneAndPassForM500FromTableLimits = (
  tableLimits: any[],
  jobNumber: string,
  zoneObj: any
) => {
  try {
    if (!Array.isArray(tableLimits) || !jobNumber || !zoneObj) {
      throw new Error("Invalid input parameters");
    }

    const record = tableLimits.find(
      (item) => item.job_number === jobNumber && item.zone_name === zoneObj
    );

    const newZoneObj = validateM500Zone(zoneObj);
    const extractedData = {
      PipeNum: isValidValue(record?.pipe_name)
        ? record.pipe_name
        : `Pipe ${newZoneObj?.PipeNum}`,
      PassNum: isValidValue(record?.pass_name)
        ? record.pass_name
        : `Pass ${newZoneObj?.PassNum}`,
      ZoneNum: isValidValue(record?.m500_zone)
        ? record.m500_zone
        : `Zone ${newZoneObj?.ZoneNum}`,
    };

    return extractedData;
  } catch (error) {
    return { PipeNum: "-", PassNum: "-", ZoneNum: "-" };
  }
};

// New function to extract M500 zone details directly from the data object
export const extractM500ZoneDetailsFromData = (data: any) => {
  try {
    if (!data) {
      return { PipeNum: "-", PassNum: "-", ZoneNum: "-" };
    }

    // Parse the zone_name JSON string / object / plain label
    const zoneObj = validateM500Zone(data.zone_name);

    const extractedData = {
      PipeNum: isValidValue(data.pipe_name)
        ? data.pipe_name
        : `Pipe ${zoneObj?.PipeNum}`,
      PassNum: isValidValue(data.pass_name)
        ? data.pass_name
        : `Pass ${zoneObj?.PassNum}`,
      ZoneNum: isValidValue(data.m500_zone)
        ? data.m500_zone
        : zoneObj?.ZoneNum != null && zoneObj.ZoneNum !== "-"
          ? String(zoneObj.ZoneNum).startsWith("Zone ")
            ? String(zoneObj.ZoneNum)
            : `Zone ${zoneObj.ZoneNum}`
          : formatZoneDisplayValue(data.zone_name) || "-",
    };

    return extractedData;
  } catch (error) {
    return { PipeNum: "-", PassNum: "-", ZoneNum: "-" };
  }
};
